import React from 'react';
import { CardStatsProps } from '@/types/components';
import {
  Stat,
  StatLabel,
  StatValue,
  StatTrend,
  StatDescription,
} from '@/design-system/stat';
import { Skeleton } from '@/design-system/skeleton';
import { cn } from '@/design-system/lib/utils';

/** Icon color per stat color — icon only, never a wash. */
const ICON_COLOR_CLASSES: Record<
  NonNullable<CardStatsProps['color']>,
  string
> = {
  default: 'text-text-soft-400',
  blue: 'text-(--state-information-base)',
  green: 'text-(--state-success-base)',
  yellow: 'text-(--state-away-base)',
  red: 'text-(--state-error-base)',
  purple: 'text-accent',
};

export const CardStats: React.FC<CardStatsProps> = ({
  title,
  value,
  icon,
  subtitle,
  trend,
  color = 'default',
  size = 'medium',
  loading = false,
  onClick,
  className = '',
}) => {
  const baseClasses =
    'rounded-lg border border-stroke-soft-200 bg-bg-white-0 transition-all duration-200';

  const interactiveClasses = onClick
    ? 'cursor-pointer hover:shadow-custom-shadows-medium hover:border-stroke-sub-300'
    : '';

  const sizeClasses = {
    small: 'p-4',
    medium: 'p-6',
    large: 'p-8',
  };

  const iconSizes = {
    small: 'text-[20px]',
    medium: 'text-[24px]',
    large: 'text-[28px]',
  };

  const valueSizes = {
    small: 'text-xl leading-7',
    medium: 'text-2xl leading-8',
    large: 'text-3xl leading-9',
  };

  if (loading) {
    return (
      <div
        className={cn(baseClasses, sizeClasses[size], className)}
      >
        <div className="flex items-center">
          <Skeleton className="size-6 shrink-0" />
          <div className="ml-4 flex-1">
            <Skeleton className="mb-2 h-4 w-3/4" />
            <Skeleton className="h-8 w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        baseClasses,
        sizeClasses[size],
        interactiveClasses,
        className
      )}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex flex-1 items-center">
          {icon && (
            <div className="flex-shrink-0">
              <i
                className={cn(icon, iconSizes[size], ICON_COLOR_CLASSES[color])}
              ></i>
            </div>
          )}
          <Stat className={icon ? 'ml-4' : ''}>
            <StatLabel>{title}</StatLabel>
            <StatValue className={cn('tracking-tight', valueSizes[size])}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </StatValue>
            {subtitle && (
              <StatDescription className="text-text-soft-400">
                {subtitle}
              </StatDescription>
            )}
          </Stat>
        </div>

        {trend && (
          <StatTrend
            trend={trend.direction}
            value={`${Math.abs(trend.value)}%`}
          >
            {trend.period && (
              <span className="font-normal text-text-sub-600">
                {trend.period}
              </span>
            )}
          </StatTrend>
        )}
      </div>
    </div>
  );
};

export default CardStats;
